import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Slide, AlertTitle, IconButton, Box } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

// Create the context
const ToastContext = createContext(null);

// Slide transition component
function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

/**
 * Toast Provider Component
 * Provides global toast notifications throughout the app
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  // Add a new toast
  const showToast = useCallback((message, options = {}) => {
    const {
      severity = 'info', // 'success' | 'error' | 'warning' | 'info'
      title = null,
      duration = 5000,
      position = { vertical: 'bottom', horizontal: 'right' },
    } = options;

    const id = Date.now() + Math.random();
    
    setToasts(prev => [...prev, {
      id,
      message,
      severity,
      title,
      duration,
      position,
      open: true,
    }]);

    // Auto-remove after duration
    if (duration !== null) {
      setTimeout(() => {
        removeToast(id);
      }, duration + 500); // Extra time for animation
    }

    return id;
  }, []);

  // Remove a toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(toast => 
      toast.id === id ? { ...toast, open: false } : toast
    ));
    
    // Remove from array after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300);
  }, []);

  // Convenience methods
  const success = useCallback((message, options = {}) => {
    return showToast(message, { ...options, severity: 'success' });
  }, [showToast]);

  const error = useCallback((message, options = {}) => {
    return showToast(message, { ...options, severity: 'error', duration: 7000 });
  }, [showToast]);

  const warning = useCallback((message, options = {}) => {
    return showToast(message, { ...options, severity: 'warning' });
  }, [showToast]);

  const info = useCallback((message, options = {}) => {
    return showToast(message, { ...options, severity: 'info' });
  }, [showToast]);

  // Handle close
  const handleClose = (id) => (event, reason) => {
    if (reason === 'clickaway') return;
    removeToast(id);
  };

  const value = {
    showToast,
    success,
    error,
    warning,
    info,
    removeToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      
      {/* Render all toasts */}
      <Box sx={{ position: 'fixed', zIndex: 9999 }}>
        {toasts.map((toast, index) => (
          <Snackbar
            key={toast.id}
            open={toast.open}
            autoHideDuration={toast.duration}
            onClose={handleClose(toast.id)}
            anchorOrigin={toast.position}
            TransitionComponent={SlideTransition}
            sx={{
              bottom: { xs: 24 + (index * 70) },
            }}
          >
            <Alert
              severity={toast.severity}
              variant="filled"
              onClose={handleClose(toast.id)}
              sx={{
                width: '100%',
                minWidth: 300,
                maxWidth: 450,
                boxShadow: 3,
                '& .MuiAlert-message': {
                  maxWidth: 350,
                },
              }}
              action={
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => removeToast(toast.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {toast.title && <AlertTitle>{toast.title}</AlertTitle>}
              {toast.message}
            </Alert>
          </Snackbar>
        ))}
      </Box>
    </ToastContext.Provider>
  );
}

/**
 * Hook to use toast notifications
 * @returns Toast notification methods
 * 
 * @example
 * const toast = useToast();
 * toast.success('Operation completed!');
 * toast.error('Something went wrong');
 * toast.warning('This action is irreversible');
 * toast.info('Here is some information');
 */
export function useToast() {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  
  return context;
}

export default ToastProvider;
